// src/lib/aiService.ts
// Frontend AI client. It never calls Gemini directly and never reads a Gemini
// key. The only allowed browser env is VITE_AI_API_URL.

import { useProjectStore } from '../store/useProjectStore';
import type { VisionEstimateResult } from '../domain/visionEstimateSchema';
import { requestVisionEstimate } from './visionEstimateClient';
import { getJsonAuthHeaders } from './authHeaders';
import { apiBaseUrl, buildApiUrl } from '../config/apiBaseUrl';
import {
  AI_ENDPOINTS,
  AI_TIMEOUTS_MS,
  type AIEndpointPath,
} from '../config/aiRuntimeConfig';
import { logOperationalError } from './operationalErrorLog';

const AI_API_URL = apiBaseUrl;
const IS_DEV = Boolean((import.meta as any).env?.DEV);
export const BACKEND_ONLINE_AI_LIMIT_MESSAGE =
  'Asystent online jest chwilowo niedostępny. Ręczna wycena i Tarcza Marży działają dalej.';

export type AIProvider = 'gemini' | 'groq';

export type AIAvailabilityStatus =
  | 'backend_online'
  | 'ai_configured_not_tested'
  | 'ai_unsupported_location'
  | 'ai_error'
  | 'ai_online'
  | 'provider_quota_exhausted'
  | 'app_daily_limit_reached'
  | 'ai_offline';

const LIMIT_OR_OFFLINE_REASONS = new Set([
  'provider_quota_exhausted',
  'app_daily_limit_reached',
  'missing_ai_api_key',
  'missing_gemini_key',
  'provider_unavailable',
  'all_ai_providers_failed',
  'unsupported_location',
  'timeout',
  'error',
  'ai_backend_error',
  'ai_offline',
]);
const APP_DAILY_LIMIT_MESSAGE = 'Asystent online jest chwilowo niedostępny. Ręczna wycena i Tarcza Marży działają dalej.';
const OFFLINE_MESSAGE = 'Asystent online jest chwilowo niedostępny. Ręczna wycena i Tarcza Marży działają dalej.';
const UNSUPPORTED_LOCATION_MESSAGE = 'AI niedostępne w regionie API. Wycena ręczna działa.';

export const MARKET_PRICES = {
  GRUNTOWANIE: 15,
  GLADZ_SZPACHLOWA: 45,
  MARGIN_PERCENT: 0.25,
  MIN_MARGIN: 1200,
  LABOR_RATE: 38,
  MATERIAL_RATE: 12,
  RISK_BUFFER: 5,
} as const;

export type OfflineAIResult = {
  ok: false;
  offline: true;
  message: string;
  status: AIAvailabilityStatus;
  reason?: string;
  retryAfter?: string;
  provider?: AIProvider;
  fallbackAvailable?: boolean;
  userMessage?: string;
};

export interface PNBViolation {
  type: string;
  severity: 'critical' | 'warning';
  description: string;
  estimatedCost: number;
}

export interface AIGeometryResult {
  obwod: number;
  wysokosc: number;
  powPodlogi: number;
  powScian: number;
  powScianNetto: number;
  otworyStandardoweSzt: number;
  otworyDuzeM2: number;
  glifyMb: number;
  referencjaSkali?: string;
  confidence: number;
  isOutdoor: boolean;
  detectedMaterials?: VisionSuggestion['detectedMaterials'];
  referenceObjects?: VisionSuggestion['referenceObjects'];
  notes?: string[];
  detectedGaps: string[];
  surfaceCondition:
    | 'raw_plaster'
    | 'painted'
    | 'damaged'
    | 'wet'
    | 'goly_tynk'
    | 'grunt'
    | 'gladka'
    | 'stare_plytki'
    | 'unknown';
  pnbViolations?: PNBViolation[];
}

export interface VisionSuggestion {
  provider?: AIProvider;
  roomType?: VisionEstimateResult['roomType'];
  visibleWalls?: number;
  sockets?: number;
  suggestedScope?: VisionEstimateResult['suggestedScope'];
  observations?: string[];
  warnings?: string[];
  requiresManualConfirmation?: string[];
  suggestedLength?: number | null;
  suggestedWidth?: number | null;
  suggestedHeight?: number | null;
  doorCount?: number;
  bigWindowsArea?: number;
  standardWindowCount?: number;
  isOutdoor?: boolean;
  surfaceCondition?: string;
  detectedMaterials?: Array<{
    type: 'ready_plaster_bucket' | 'drywall_board' | 'paint_bucket' | 'unknown';
    count: number;
    estimatedUnitSize: string | null;
    confidence: number;
  }>;
  referenceObjects?: Array<{
    type: 'door' | 'socket' | 'drywall_board' | 'manual_reference';
    assumedRealSize: string;
    confidence: number;
  }>;
  confidence?: number;
  notes?: string[];
}

export interface TechnologyGap {
  id: string;
  name: string;
  description: string;
  materialCost: number;
  laborCost: number;
  riskCost: number;
  isCritical: boolean;
}

export interface AIChatAction {
  type: 'add_gap' | 'adjust_margin' | 'info';
  label: string;
  payload?: Record<string, unknown>;
}

export interface AIChatResponse {
  text: string;
  action?: AIChatAction;
  offline?: boolean;
  status?: AIAvailabilityStatus;
  reason?: string;
  provider?: AIProvider;
}

export type AIChatContext = {
  projectName?: string;
  trade?: string;
  tradeEngine?: string;
  scopeText?: string;
  roomType?: string;
  estimateMeta?: object;
  branza?: string | null;
  items?: object[];
  total?: number;
  area?: number;
  profitLeaks?: object[];
  materialSuggestions?: object[];
  scheduleEstimate?: object;
  itemsCount?: number;
  hasGrunt?: boolean;
  hasHydro?: boolean;
};

function offlineResult(
  reason?: string,
  retryAfter?: string,
  options?: {
    aiStatus?: string;
    provider?: AIProvider;
    fallbackAvailable?: boolean;
    userMessage?: string;
  }
): OfflineAIResult {
  const status = toAIAvailabilityStatus(reason, options?.aiStatus);
  const message =
    status === 'ai_unsupported_location'
      ? UNSUPPORTED_LOCATION_MESSAGE
      : reason === 'app_daily_limit_reached'
      ? APP_DAILY_LIMIT_MESSAGE
      : reason === 'provider_quota_exhausted'
        ? BACKEND_ONLINE_AI_LIMIT_MESSAGE
        : options?.userMessage || OFFLINE_MESSAGE;

  return {
    ok: false,
    offline: true,
    message,
    status,
    reason,
    retryAfter,
    provider: options?.provider,
    fallbackAvailable: options?.fallbackAvailable,
    userMessage: options?.userMessage,
  };
}

function toAIAvailabilityStatus(reason?: string, aiStatus?: string): AIAvailabilityStatus {
  if (aiStatus === 'unsupported_location' || reason === 'unsupported_location') {
    return 'ai_unsupported_location';
  }
  if (aiStatus === 'configured_not_tested') return 'ai_configured_not_tested';
  if (aiStatus === 'error') return 'ai_error';
  if (reason === 'provider_quota_exhausted') return 'provider_quota_exhausted';
  if (reason === 'app_daily_limit_reached') return 'app_daily_limit_reached';
  return 'ai_error';
}

function isAIProvider(value: unknown): value is AIProvider {
  return value === 'gemini' || value === 'groq';
}

function logProviderSuccess(path: AIEndpointPath, provider: AIProvider): void {
  if (!IS_DEV) return;

  console.log('[AI_PROVIDER_STATUS]', {
    provider,
    success: true,
    endpoint: path,
  });
}

function warnLimitOrOffline(path: AIEndpointPath, reason?: string, statusCode?: number): void {
  if (!reason || !LIMIT_OR_OFFLINE_REASONS.has(reason)) return;

  console.warn('[AI_STATUS] provider unavailable', {
    backendUrl: AI_API_URL,
    path,
    statusCode,
    reason,
  });
}

function markAIUnavailable(): void {
  useProjectStore.getState().setAIAvailable(false);
}

function markAIAvailable(): void {
  useProjectStore.getState().setAIAvailable(true);
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs = AI_TIMEOUTS_MS.request
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

async function postAI<T>(path: AIEndpointPath, body: object): Promise<T | OfflineAIResult> {
  try {
    const headers = await getJsonAuthHeaders();
    const response = await fetchWithTimeout(buildApiUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      void logOperationalError('api', `ai_http_${response.status}`);
      warnLimitOrOffline(path, errorPayload?.reason, response.status);
      markAIUnavailable();
      return offlineResult(errorPayload?.reason, errorPayload?.retryAfter, {
        aiStatus: errorPayload?.aiStatus,
        provider: isAIProvider(errorPayload?.provider) ? errorPayload.provider : undefined,
        fallbackAvailable: Boolean(errorPayload?.fallbackAvailable),
        userMessage: typeof errorPayload?.userMessage === 'string' ? errorPayload.userMessage : undefined,
      });
    }

    const data = await response.json();
    if (
      data?.error ||
      data?.offline ||
      data?.ok === false ||
      LIMIT_OR_OFFLINE_REASONS.has(data?.reason)
    ) {
      void logOperationalError('api', 'ai_offline_response');
      warnLimitOrOffline(path, data?.reason, response.status);
      markAIUnavailable();
      return offlineResult(data?.reason, data?.retryAfter, {
        aiStatus: data?.aiStatus,
        provider: isAIProvider(data?.provider) ? data.provider : undefined,
        fallbackAvailable: Boolean(data?.fallbackAvailable),
        userMessage: typeof data?.userMessage === 'string' ? data.userMessage : undefined,
      });
    }

    if (isAIProvider(data?.provider)) {
      logProviderSuccess(path, data.provider);
    }

    markAIAvailable();
    return data as T;
  } catch (err) {
    console.warn('[aiService] AI backend unavailable:', err);
    void logOperationalError('api', 'ai_timeout_or_network_error');
    markAIUnavailable();
    return offlineResult(undefined, undefined, { aiStatus: 'error' });
  }
}

function toLegacyGeometryResult(payload: VisionSuggestion): AIGeometryResult {
  const hasMeasuredArea =
    typeof payload.suggestedLength === 'number' &&
    payload.suggestedLength > 0 &&
    typeof payload.suggestedWidth === 'number' &&
    payload.suggestedWidth > 0;
  const floorArea = hasMeasuredArea
    ? Math.round(payload.suggestedLength * payload.suggestedWidth * 10) / 10
    : 0;

  return {
    obwod: 0,
    wysokosc: payload.suggestedHeight ?? 0,
    powPodlogi: floorArea,
    powScian: 0,
    powScianNetto: 0,
    otworyStandardoweSzt: payload.standardWindowCount ?? 0,
    otworyDuzeM2: payload.bigWindowsArea ?? 0,
    glifyMb: 0,
    referencjaSkali: 'AI sensor',
    confidence: payload.confidence ?? 0,
    isOutdoor: payload.isOutdoor ?? false,
    detectedMaterials: payload.detectedMaterials ?? [],
    referenceObjects: payload.referenceObjects ?? [],
    notes: payload.notes ?? [],
    detectedGaps: [],
    surfaceCondition: normalizeSurfaceCondition(payload.surfaceCondition),
  };
}

function normalizeSurfaceCondition(value: unknown): AIGeometryResult['surfaceCondition'] {
  const allowed = [
    'raw_plaster',
    'painted',
    'damaged',
    'wet',
    'goly_tynk',
    'grunt',
    'gladka',
    'stare_plytki',
    'unknown',
  ];
  return allowed.includes(String(value)) ? (value as AIGeometryResult['surfaceCondition']) : 'unknown';
}

export async function analyzePhotoWithAI(
  photoDataUrl: string,
  _description: string
): Promise<AIGeometryResult | null> {
  const result = await analyzeRoomVision({
    imageBase64: photoDataUrl,
  });

  if ('offline' in result) {
    throw new Error(result.message);
  }

  return toLegacyGeometryResult(result);
}

export async function analyzeRoomVision(input: {
  imageBase64: string;
  mimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
  knownDimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
  trade?: string;
  scope?: string;
  floorAreaM2?: number;
  city?: string;
  region?: string;
}): Promise<VisionSuggestion | OfflineAIResult> {
  const result = await requestVisionEstimate({
    imageBase64: input.imageBase64,
    mimeType: input.mimeType ?? 'image/jpeg',
    projectContext: {
      trade: input.trade,
      scope: input.scope,
      // Wymiary podane przez wykonawce byly przyjmowane i gubione przed
      // wyslaniem. Model dostaje je jako KONTEKST skali, nigdy jako polecenie
      // pomiaru — metraz nadal podaje i zatwierdza wykonawca.
      floorAreaM2: input.floorAreaM2,
      knownDimensions: input.knownDimensions,
      city: input.city,
      region: input.region,
    },
  });

  if (!result.ok) {
    markAIUnavailable();
    return offlineResult(result.reason, undefined, {
      provider: isAIProvider(result.provider) ? result.provider : undefined,
      fallbackAvailable: true,
      userMessage: result.error,
    });
  }

  markAIAvailable();
  return toVisionSuggestion(
    result.result,
    isAIProvider(result.provider) ? result.provider : undefined
  );
}

function toVisionSuggestion(result: VisionEstimateResult, provider?: AIProvider): VisionSuggestion {
  const notes = [
    ...result.observations,
    ...result.warnings,
    ...result.requiresManualConfirmation.map((item) => `Do potwierdzenia: ${item}`),
  ];

  return {
    provider,
    roomType: result.roomType,
    visibleWalls: result.detectedElements.visibleWalls,
    sockets: result.detectedElements.sockets ?? 0,
    suggestedScope: result.suggestedScope,
    observations: result.observations,
    warnings: result.warnings,
    requiresManualConfirmation: result.requiresManualConfirmation,
    suggestedLength: null,
    suggestedWidth: null,
    suggestedHeight: null,
    doorCount: result.detectedElements.doors,
    standardWindowCount: result.detectedElements.windows,
    bigWindowsArea: 0,
    isOutdoor: false,
    surfaceCondition: mapVisionSurfaceCondition(result.surfaceCondition),
    confidence: Math.round(result.confidence * 100),
    notes,
    detectedMaterials: [],
    referenceObjects: result.detectedElements.sockets
      ? [{
          type: 'socket',
          assumedRealSize: 'widoczne gniazdko, bez skali pomiarowej',
          confidence: result.confidence,
        }]
      : [],
  };
}

function mapVisionSurfaceCondition(condition: VisionEstimateResult['surfaceCondition']): string {
  if (condition === 'dobry') return 'painted';
  if (condition === 'trudny') return 'damaged';
  if (condition === 'sredni') return 'unknown';
  return 'unknown';
}

export async function askAIInspector(
  question: string,
  context: AIChatContext
): Promise<AIChatResponse> {
  const result = await postAI<{ reply: string; provider?: AIProvider }>(AI_ENDPOINTS.chat, {
    message: question,
    context: {
      projectName: context.projectName,
      trade: context.trade ?? context.tradeEngine ?? context.branza ?? undefined,
      tradeEngine: context.tradeEngine,
      scopeText: context.scopeText,
      roomType: context.roomType,
      estimateMeta: context.estimateMeta,
      items: context.items,
      total: context.total,
      area: context.area,
      profitLeaks: context.profitLeaks,
      materialSuggestions: context.materialSuggestions,
      scheduleEstimate: context.scheduleEstimate,
      persona:
        'Jesteś doświadczonym majstrem i cyfrowym inspektorem finansowym. Mów krótko, budowlanie i w kontekście aktualnej wyceny. Wskazuj ryzyka jako sugestie, ale nigdy nie narzucaj decyzji. Nie wymyślaj robót spoza zakresu. Nie licz finalnej ceny — robi to engine.',
    },
  });

  if ('offline' in result) {
    return {
      text: result.message,
      offline: true,
      status: result.status,
      reason: result.reason,
    };
  }

  return {
    text: result.reply || OFFLINE_MESSAGE,
    status: 'ai_online',
    provider: result.provider,
  };
}

export async function pingAI(): Promise<boolean> {
  if (!navigator.onLine) {
    markAIUnavailable();
    return false;
  }

  try {
    const response = await fetchWithTimeout(buildApiUrl(AI_ENDPOINTS.health), {
      method: 'GET',
    });
    const ok = response.ok;
    if (!ok) markAIUnavailable();
    return ok;
  } catch {
    markAIUnavailable();
    return false;
  }
}
